<template>
  <b-modal
    :active.sync="isModalActive"
    has-modal-card
    class="sheet-table-config-modal"
    :can-cancel="['escape']"
  >
    <div class="modal-card">
      <header class="modal-card-head">
        <h1 class="is-title-header modal-card-title">
          Sheet & Table Configuration
        </h1>
      </header>
      <section class="modal-card-body">
        <!-- Sheet Name Section -->
        <div class="sheet-section mb-5">
          <h3 class="subtitle is-5">Sheet Information</h3>
          <b-field label="Sheet Name">
            <b-input
              v-model="localSheetName"
              placeholder="Enter sheet name (e.g., Sheet 1, Summary, Details)"
              class="is-medium"
            />
          </b-field>
          <div
            v-if="sheetNameError"
            class="notification is-danger is-light mt-2 p-2"
          >
            <strong>Error:</strong> {{ sheetNameError }}
          </div>
        </div>

        <!-- Tables Section -->
        <div class="tables-section">
          <div
            class="is-flex is-justify-content-space-between is-align-items-center mb-3"
          >
            <h3 class="subtitle is-5">Tables</h3>
            <b-button
              type="is-primary"
              size="is-small"
              icon-left="plus"
              @click="addNewTable"
            >
              Add Table
            </b-button>
          </div>

          <!-- List of Tables -->
          <div v-if="localTables.length > 0" class="tables-list">
            <div
              v-for="(table, tableIdx) in localTables"
              :key="tableIdx"
              class="table-card mb-2 p-2 has-background-light"
            >
              <div
                class="is-flex is-justify-content-space-between is-align-items-center mb-2"
              >
                <h4 class="subtitle is-6">
                  Table {{ tableIdx + 1 }}: {{ table.tableName || "(Unnamed)" }}
                </h4>
                <b-button
                  type="is-danger"
                  size="is-small"
                  icon-left="delete"
                  @click="deleteTable(tableIdx)"
                >
                  Delete Table
                </b-button>
              </div>

              <b-field label="Table Name">
                <b-input
                  v-model="table.tableName"
                  placeholder="Enter table name"
                  class="is-medium"
                />
              </b-field>

              <div class="header-section">
                <h5 class="column-header">Columns</h5>
                <div
                  v-for="(header, hIdx) in table.headers"
                  :key="hIdx"
                  class="header-root"
                >
                  <HeaderNode
                    :header="header"
                    :header-path="[tableIdx, hIdx]"
                    @add-sub-header="addSubHeader"
                    @remove-header="removeHeader"
                  />
                </div>
                <b-button
                  type="is-primary"
                  size="is-small"
                  icon-left="plus"
                  @click="addHeader(tableIdx)"
                  class="mt-1"
                >
                  Add Column
                </b-button>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div
            v-else
            class="has-text-centered has-background-grey-lighter p-3 mb-2"
          >
            <p class="has-text-grey">
              No tables added yet. Click "Add Table" to create one.
            </p>
          </div>
        </div>
      </section>

      <footer class="modal-card-foot is-flex is-justify-content-flex-end">
        <b-button
          type="is-primary"
          @click="submitConfig"
          :disabled="!localSheetName || localTables.length === 0"
          class="mr-2"
        >
          Save Sheet & Tables
        </b-button>
        <b-button type="is-normal" @click="closeModal">Cancel</b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
const HeaderNode = {
  name: "HeaderNode",
  props: {
    header: { type: Object, required: true },
    headerPath: { type: Array, required: true }
  },
  methods: {
    addSubHeader() {
      this.$emit("add-sub-header", this.headerPath);
    },
    removeHeader() {
      this.$emit("remove-header", this.headerPath);
    }
  },
  render(h) {
    const depth = this.headerPath.length - 1;
    let placeholder = "Add Column Name";
    if (depth === 1) placeholder = "Add Column Name";
    else if (depth === 2) placeholder = "Add Column's Sub Name";
    else if (depth === 3) placeholder = `Add Column's Sub's Sub Name`;
    else if (depth === 4) placeholder = `Add Column's Sub's Sub's Sub Name`;

    const headerRowChildren = [
      h("b-input", {
        props: { value: this.header.name },
        attrs: { placeholder: placeholder },
        on: {
          input: val => {
            this.header.name = val;
          }
        },
        class: "is-medium header-input",
        style: {
          width: "100%",
          marginRight: "8px",
          marginLeft:
            this.headerPath.length > 2
              ? (this.headerPath.length - 2) * 24 + "px"
              : "0px"
        }
      })
    ];

    // Only show add button if depth is less than 4
    if (this.headerPath.length < 4) {
      headerRowChildren.push(
        h(
          "b-button",
          {
            props: { type: "is-primary", size: "is-small", iconLeft: "plus" },
            class: "ml-1",
            on: {
              click: () => this.addSubHeader()
            }
          },
          [h("span", this.headerPath.length === 1 ? "Add Sub" : "Add Sub")]
        )
      );
    }

    headerRowChildren.push(
      h(
        "b-button",
        {
          props: { type: "is-danger", size: "is-small", iconLeft: "delete" },
          class: "ml-1",
          on: {
            click: () => this.removeHeader()
          }
        },
        []
      )
    );

    const children = [
      h(
        "div",
        {
          class: "header-row",
          style: { display: "flex", marginBottom: "8px", alignItems: "center" }
        },
        headerRowChildren
      )
    ];

    if (this.header.subHeaders && this.header.subHeaders.length > 0) {
      this.header.subHeaders.forEach((child, idx) => {
        children.push(
          h(HeaderNode, {
            props: {
              header: child,
              headerPath: [...this.headerPath, idx]
            },
            on: {
              "add-sub-header": path => this.$emit("add-sub-header", path),
              "remove-header": path => this.$emit("remove-header", path)
            }
          })
        );
      });
    }

    return h("div", { class: "header-node" }, children);
  }
};

export default {
  name: "SheetTableConfig",
  components: {
    HeaderNode
  },
  props: {
    isActive: Boolean,
    sheetName: String,
    existingSheetNames: Array,
    existingTables: Array
  },
  data() {
    return {
      isModalActive: this.isActive,
      localSheetName: this.sheetName || "",
      localTables: [], // Array of tables {tableName, headers}
      sheetNameError: ""
    };
  },
  watch: {
    isActive: {
      immediate: true,
      handler(val) {
        this.isModalActive = val;
        if (val) {
          this.localSheetName = this.sheetName || "";
          this.loadExistingTables();
          this.sheetNameError = "";
        }
      }
    },
    existingTables: {
      deep: true,
      handler() {
        if (this.isModalActive) {
          this.loadExistingTables();
        }
      }
    }
  },
  methods: {
    loadExistingTables() {
      const hasExisting = this.existingTables && this.existingTables.length;
      if (hasExisting) {
        // Normalize possible shapes (table_json nested, plain tableJson, or direct structure)
        this.localTables = this.existingTables.map(table => {
          const tableJson =
            table.table_json?.table_json ||
            table.table_json ||
            table.tableJson ||
            table;

          return {
            tableName: tableJson.tableName || table.table_name || "",
            headers: tableJson.headers
              ? JSON.parse(JSON.stringify(tableJson.headers))
              : table.headers
              ? JSON.parse(JSON.stringify(table.headers))
              : []
          };
        });
      } else {
        this.localTables = [];
      }
    },
    addNewTable() {
      this.localTables.push({
        tableName: "",
        headers: [{ name: "", key: "", subHeaders: [] }]
      });
    },
    deleteTable(tableIdx) {
      this.$buefy.dialog.confirm({
        title: "Delete Table",
        message: `Are you sure you want to delete "${this.localTables[tableIdx]
          .tableName || "Unnamed"}" table?`,
        confirmText: "Delete",
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => {
          this.localTables.splice(tableIdx, 1);
        }
      });
    },
    addHeader(tableIdx) {
      this.localTables[tableIdx].headers.push({
        name: "",
        key: "",
        subHeaders: []
      });
    },
    addSubHeader(path) {
      // path is [tableIdx, headerIdx, ...]
      const tableIdx = path[0];
      const target = this.getHeaderByPath(tableIdx, path.slice(1));
      if (!target.subHeaders) {
        this.$set(target, "subHeaders", []);
      }
      target.subHeaders.push({
        name: "",
        key: "",
        subHeaders: []
      });
    },
    removeHeader(path) {
      // path is [tableIdx, headerIdx, ...]
      const tableIdx = path[0];
      const headerPath = path.slice(1);

      if (headerPath.length === 1) {
        this.localTables[tableIdx].headers.splice(headerPath[0], 1);
      } else {
        const parent = this.getHeaderByPath(tableIdx, headerPath.slice(0, -1));
        parent.subHeaders.splice(headerPath[headerPath.length - 1], 1);
      }
    },
    getHeaderByPath(tableIdx, path) {
      let current = this.localTables[tableIdx].headers;
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]].subHeaders;
      }
      return current[path[path.length - 1]];
    },
    submitConfig() {
      this.sheetNameError = "";

      // Validate sheet name
      if (!this.localSheetName || this.localSheetName.trim() === "") {
        this.sheetNameError = "Sheet name is required.";
        return;
      }

      // Check for duplicate sheet names
      const isDuplicate = this.existingSheetNames.some(
        name =>
          name.toLowerCase() === this.localSheetName.toLowerCase() &&
          name !== this.sheetName // Allow same name if editing
      );

      if (isDuplicate) {
        this.sheetNameError = `A sheet named "${this.localSheetName}" already exists.`;
        return;
      }

      // Validate all tables
      for (let i = 0; i < this.localTables.length; i++) {
        const table = this.localTables[i];

        if (!table.tableName || table.tableName.trim() === "") {
          this.$buefy.dialog.alert({
            title: "Error",
            message: `Table ${i + 1}: Table name is required.`,
            type: "is-danger"
          });
          return;
        }

        if (table.headers.length === 0) {
          this.$buefy.dialog.alert({
            title: "Error",
            message: `Table "${table.tableName}": At least one column is required.`,
            type: "is-danger"
          });
          return;
        }
      }

      // Emit the configuration
      this.$emit("save", {
        sheetName: this.localSheetName,
        tables: this.localTables
      });
      this.closeModal();
    },
    closeModal() {
      this.isModalActive = false;
      this.$emit("cancel");
    }
  }
};
</script>

<style scoped>
.sheet-section {
  border-bottom: 2px solid #e8e8e8;
  padding-bottom: 10px;
}

.tables-section {
  margin-top: 10px;
}

.table-card {
  border-left: 4px solid #3273dc;
  border-radius: 4px;
  position: relative;
}

.header-section {
  margin-top: 10px;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
}

.column-header {
  font-size: 13px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #333;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.header-root {
  margin-bottom: 5px;
}

.header-node {
  margin-bottom: 4px;
}

.header-row {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.header-input {
  flex: 1;
}

.modal-card-title {
  font-size: 18px;
  font-weight: bold;
}

.is-title-header {
  color: #363636;
}

.subtitle {
  color: #363636;
}
</style>
